import { z } from 'zod'
import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  seasonRebidDtoSchema,
  seasonReminderDtoSchema,
  type SeasonRebidDto,
  type SeasonReminderDto,
} from '@lib/dto/season-reminder.dto'
import type { SeasonReminderActionInput } from '@lib/input/season-reminder.input'
import type { ISeasonReminderService } from '@/types/SeasonReminderService.types'

/**
 * Service for the season reminder endpoints: the "Coming up" list of work
 * accepted proposals estimated for a season that is close
 * (docs/seasonal-work.md). Responses are validated against the shared `@lib`
 * schemas at the boundary.
 */
class SeasonReminderService implements ISeasonReminderService {
  async list(): Promise<SeasonReminderDto[]> {
    try {
      return await api.get('/season-reminders', z.array(seasonReminderDtoSchema))
    } catch (error) {
      logger.error('Failed to load season reminders', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async dismiss(input: SeasonReminderActionInput): Promise<void> {
    try {
      await api.postNoContent('/season-reminders/dismiss', input)
    } catch (error) {
      logger.error('Failed to dismiss a season reminder', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async rebid(input: SeasonReminderActionInput): Promise<SeasonRebidDto> {
    try {
      return await api.post('/season-reminders/rebid', input, seasonRebidDtoSchema)
    } catch (error) {
      logger.error('Failed to start a rebid', { error: toErrorMessage(error) })
      throw error
    }
  }
}

export const seasonReminderService = new SeasonReminderService()
